import React from "react";
import BaseListPresenter from "../../../base/BaseListPresenter";
import { Button, dialog } from "nq-component";
import InputFactory from "../../../components/InputFactory";
import { getAllDatesFromRanges, buildDateConditions, batchUpdateDTRRecords } from "../../../helper";

class OBManagementPresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
    exportCSVUseCase,
    addSchemaUseCase,
    updateSchemaUseCase,
    deleteSchemaUseCase
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.exportCSVUseCase = exportCSVUseCase;
    this.addSchemaUseCase = addSchemaUseCase;
    this.updateSchemaUseCase = updateSchemaUseCase;
    this.deleteSchemaUseCase = deleteSchemaUseCase;
    this.view = view;
  }

  init() {
    this.limit = 10;
    this.where = {};
    this.search = {};
    this.filter = {};
    this.include = ["all"];
    this.keys = undefined; // if keys are specified, only those keys will be returned
    this.sort = {};
    this.progress = true;
    this.reset();
  }

  createQuery() {
    const skip = (this.current - 1) * this.limit;
    const query = {
      limit: this.limit,
      skip: skip,
      where: { ...this.where, ...this.search, ...this.filter },
      include: this.include,
    };

    if (this.sort) {
      query.sort = this.sort;
    }
    const keys = this.keys || this.view.getKeys() || [];
    if (keys.length > 0) {
      query.keys = keys;
    }
    return query;
  }

  async findObjects() {
    const collection = this.view.getCollectionName();
    const query = this.createQuery();
    try {
      this.showProgress();
      this.findObjectUseCase.abort();
      const objects = await this.findObjectUseCase.execute(collection, query);
      this.objects = this.objects.concat(objects);
      this.view.setObjects(objects);
      this.view.setCurrent(this.current);
      this.hideProgress();
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
    this.progress = false;
  }

  setCurrentPage(page) {
    this.current = page;
    this.documents = [];
    this.view.setCurrent(page);
    this.view.setObjects([]);
    this.findObjects();
  }

  DialogConfirmation(title, message, onConfirm, status) {
    let reason = ""; // Local variable to store the reason input value

    dialog.fire({
      html: (
        <div className="p-2">
          <div className="row p-1">
            <h4>{title}</h4>
            <hr />
            <p style={{ fontSize: "13px", padding: "1rem" }}>
              {message}
            </p>
            {status === "Disapproved" && (
              <div className="pe-4 ps-4 mb-3">
                <label className="mb-2 fs-6">
                  Reason for Disapproving <span className="text-danger">*</span>
                </label>
                <InputFactory
                  type="String"
                  field="reason"
                  _type="Text"
                  onChange={(value) => (reason = value)}
                  // className="form-control"
                  required
                  rows={2}
                />
              </div>
            )}
            <hr />
          </div>
          <div className="w-100 d-flex justify-content-end gap-2 pe-3 pb-3">
            {onConfirm ? (
              <>
                <Button
                  className="btn btn-light fs-sm border-primary"
                  onClick={() => dialog.close()}
                >
                  Cancel
                </Button>
                <Button
                  className="btn btn-primary"
                  onClick={async () => {
                    if (status === "Disapproved" && !reason.trim()) {
                      this.view.showError("Please provide a reason for disapproving.");
                      return;
                    }
                    dialog.close();
                    await onConfirm(reason);
                  }}
                >
                  Confirm
                </Button>
              </>
            ) : (
              <Button
                className="btn btn-primary"
                onClick={() => dialog.close()}
              >
                OK
              </Button>
            )}
          </div>
        </div>
      ),
      footer: false,
    });
  }


  handleStatusChange(data, newStatus) {
    if (data.status === newStatus) {
      this.DialogConfirmation(
        `Already ${newStatus}`,
        `This OB request has already been ${newStatus.toLowerCase()}.`,
        null,
        newStatus
      );
      return;
    }
  
    const message = newStatus === "Disapproved"
      ? "Are you sure you want to disapprove this OB request? Please provide a reason before proceeding."
      : `Are you sure you want to ${newStatus.toLowerCase()} this OB request? Click Confirm to continue!`;
  
    this.DialogConfirmation(
      `${newStatus} Request`,
      message,
      (reason) => this.handleApproval(data, newStatus, reason),
      newStatus
    );
  }


  async handleApproval(data, status, reason = "") {
    console.log("data", data)
    console.log("reason", reason)
    try {
      const collection = this.view.getCollectionName();

      const requestedDates = data?.selectedDate ? getAllDatesFromRanges(data?.selectedDate) : [];
      const dateConditions = buildDateConditions(requestedDates);

      const dtrWhereClause = {
        "employee.id": data.employee.id,
      };

      if (dateConditions.length > 0) {
        dtrWhereClause.$or = dateConditions;
      }

      const [requestedOB, dtrRecord] = await Promise.all([
        this.findObjectUseCase.execute("ob_requests", {
          where: {
            id: data.id,
          },
          keys: ["status"],
        }),
        this.findObjectUseCase.execute("daily_time_record", {
          where: dtrWhereClause,
          keys: ["date", "employee", "timeRecStats"],
        }),
      ]);

      const updatedOB = {
        id: requestedOB?.[0].id,
        status: status,
      }

      const updated = {
        id: data.id,
        status: status,
      }

      if (status === "Disapproved" && reason) {
        updatedOB.reason = reason;
      }
     
      if(status === "Approved") {
        await batchUpdateDTRRecords(dtrRecord, "Official Business");
      }
      await this.upsertUseCase.execute(collection, updated);
      await this.upsertUseCase.execute("ob_requests", updatedOB);
      const message = status === "Approved" 
        ? "Successfully Approved!" 
        : "Successfully Disapproved!";
      this.view.showSuccess(message);
      this.view.refetchData();
    } catch (error) {
      this.view.showError(error);
    }
  }
 
}
export default OBManagementPresenter;
